/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {ErrorType, ResponseData} from '@dvrd/dvr-controls';
import React, {ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {validate2FaToken} from "../../../util/requestSender";
import User from "../../../models/userModel";
import ValidateTwoFactor from "./validateTwoFactor";
import {ActivateHandle} from "../../../util/interfaces";

interface Props {
    onSuccess: (user: User, jwtToken: string) => void;
    explanation?: string;
}

function ValidateTwoFactorController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {explanation, onSuccess} = props;
    const [token, setToken] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ErrorType>(null);
    const [active, setActive] = useState(false);
    const jwtToken = useRef<string | null>(null);

    function onActivate(_jwtToken: string) {
        jwtToken.current = _jwtToken;
        setActive(true);
    }

    function onClose() {
        setToken('');
        setLoading(false);
        setError(null);
        setActive(false);
        jwtToken.current = null;
    }

    function onChangeToken(token: string) {
        // Only allow numbers to be used
        if (!token.match(/^\d*$/)) return;
        setToken(token.trim());
        setError(null);
    }

    function onFocusToken() {
        setError(null);
    }

    function onSubmit() {
        if (!jwtToken.current) {
            return;
        }
        setLoading(true);
        validate2FaToken({
            data: {jwt_token: jwtToken.current, token}, callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    const {user, jwt_token} = data;
                    onClose();
                    onSuccess(user, jwt_token);
                } else setError('De inlogcode is niet correct')
            }
        });
    }

    useEffect(() => {
        if (token.length === 6) onSubmit();
    }, [token]);

    useImperativeHandle(ref, () => ({
        activate: onActivate
    }));

    return (
        <ValidateTwoFactor onChange={onChangeToken} onFocus={onFocusToken} onSubmit={onSubmit}
                           onClose={onClose} token={token} active={active} loading={loading}
                           error={error} explanation={explanation}/>
    )
}

export default forwardRef<ActivateHandle, Props>(ValidateTwoFactorController);